import {
  Component,
  OnInit,
  Inject,
  PLATFORM_ID,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { IUserSelectCai } from '../../core/interfaces/user-select-cai';
import { UserSelectCaiService } from '../../core/services/user-select-cai.service';
import { constantTxt } from '../../core/constants/constants';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-select-cai',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzTableModule,
  ],
  templateUrl: './user-select-cai.component.html',
  styleUrls: ['./user-select-cai.component.less'],
})
export class UserSelectCaiComponent implements OnInit, OnDestroy {
  groupEmail: string = '';
  form: FormGroup;
  items: IUserSelectCai[] = [];
  isHovered: boolean[] = [];
  isLoading = false;
  errorMessage = '';
  isBrowser: boolean = false;
  constant = constantTxt;
  selectedCaiItem: string = ''; // Track single selected email item (like email component)

  getAllListSubscription!: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly userSelectCaiService: UserSelectCaiService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.form = this.fb.group({
      HdItemAmount: [''],
      DoAction: [''],
    });
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return; // Skip initialization on server-side
    }

    // Load any previously selected email item from localStorage
    const storedCaiItem = localStorage.getItem('requestorRatt');
    if (storedCaiItem) {
      this.selectedCaiItem = storedCaiItem;
    }

    this.groupEmail = '';
    this.items = [];
    this.isHovered = [];
  }

  loadItems(): void {
    if (!this.isBrowser) {
      return; // Skip HTTP calls on server-side
    }

    if (!this.groupEmail || this.groupEmail.trim() === '') {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const filters = { cond: this.groupEmail || '' };

    this.getAllListSubscription = this.userSelectCaiService
      .getAllList(filters)
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          // Extract data from HttpResponse
          const data = response.body;

          let parsedData: any = data;
          let itemsArray: any[] = [];

          // Handle different response formats
          if (typeof data === 'string') {
            // Try to parse as JSON
            try {
              parsedData = JSON.parse(data);
            } catch (error) {
              this.items = [];
              this.isHovered = [];
              this.errorMessage = 'Invalid JSON format received from server';
              return;
            }
          } else if (data === null || data === undefined) {
            this.items = [];
            this.isHovered = [];
            this.errorMessage = '';
            return;
          } else {
            parsedData = data;
          }

          // Handle both array and single object responses
          if (Array.isArray(parsedData)) {
            itemsArray = parsedData;
          } else if (parsedData && typeof parsedData === 'object') {
            // Single object response - convert to array
            itemsArray = [parsedData];
          } else if (
            parsedData === null ||
            parsedData === undefined ||
            parsedData === ''
          ) {
            this.items = [];
            this.isHovered = [];
            this.errorMessage = '';
            return;
          } else {
            this.items = [];
            this.isHovered = [];
            this.errorMessage = 'Invalid response format from server';
            return;
          }

          // Handle DataTable JSON structure - extract rows
          if (itemsArray.length > 0 && typeof itemsArray[0] === 'object') {
            // Check if this looks like a DataTable structure
            const firstItem = itemsArray[0];
            if ('group_name' in firstItem && 'group_mail' in firstItem) {
              // Direct object structure - use as is
            } else if ('code' in firstItem && 'description' in firstItem) {
              // QA code structure - transform to email structure
              itemsArray = itemsArray.map((item) => ({
                group_name: item.description || item.code || 'N/A',
                group_mail: item.code || 'N/A',
              }));
            } else {
              // Might be DataTable structure - try to extract actual data
              let extractedData: any[] = [];

              // Try different extraction methods for DataTable
              if (firstItem.Rows && Array.isArray(firstItem.Rows)) {
                extractedData = firstItem.Rows;
              } else if (firstItem.rows && Array.isArray(firstItem.rows)) {
                extractedData = firstItem.rows;
              } else if (Array.isArray(firstItem)) {
                extractedData = firstItem;
              } else {
                // Try to find any array property that contains our data
                for (const key of Object.keys(firstItem)) {
                  const value = firstItem[key];
                  if (Array.isArray(value) && value.length > 0) {
                    const sampleItem = value[0];
                    if (
                      sampleItem &&
                      typeof sampleItem === 'object' &&
                      ('group_name' in sampleItem ||
                        'group_mail' in sampleItem ||
                        'code' in sampleItem)
                    ) {
                      extractedData = value;
                      break;
                    }
                  }
                }
              }

              if (extractedData.length > 0) {
                itemsArray = extractedData;

                // Check if extracted data needs transformation from QA to email format
                if (
                  extractedData[0] &&
                  'code' in extractedData[0] &&
                  'description' in extractedData[0]
                ) {
                  itemsArray = extractedData.map((item) => ({
                    group_name: item.description || item.code || 'N/A',
                    group_mail: item.code || 'N/A',
                  }));
                }
              }
            }
          }

          // Validate items have required properties
          const validationResults = itemsArray.map((item) => {
            const isObject = item && typeof item === 'object';
            const hasGroupName = 'group_name' in item;
            const hasGroupMail = 'group_mail' in item;

            return isObject && hasGroupName && hasGroupMail;
          });

          const isValidData = validationResults.every((result) => result);

          if (isValidData && itemsArray.length > 0) {
            this.items = itemsArray;
            this.isHovered = new Array(this.items.length).fill(false);
            this.errorMessage = ''; // Clear any previous error
          } else if (itemsArray.length === 0) {
            this.items = [];
            this.isHovered = [];
            this.errorMessage = ''; // Don't show error for empty results
          } else {
            this.items = [];
            this.isHovered = [];
            this.errorMessage = 'Invalid data structure received from server';
          }
        },
        error: (error) => {
          this.isLoading = false;

          let errorMsg = 'Error loading email data';
          if (error.status === 500) {
            errorMsg = 'Server error - please check the backend service';
          } else if (error.status === 404) {
            errorMsg = 'Email service endpoint not found';
          } else if (error.status === 0) {
            errorMsg =
              'Cannot connect to server - please check your connection';
          } else if (error.error?.message) {
            errorMsg = `Error: ${error.error.message}`;
          }

          this.errorMessage = errorMsg;
          this.items = [];
          this.isHovered = [];
        },
      });
  }

  onMouseOver(index?: number): void {
    if (index !== undefined) {
      this.isHovered[index] = true;
    }
  }

  onMouseOut(index: number): void {
    this.isHovered[index] = false;
  }

  onRadioButtonChange(selectedItem: IUserSelectCai): void {
    this.selectedCaiItem = selectedItem.group_mail || '';
    localStorage.setItem('requestorRatt', selectedItem.group_mail || '');
    window.close();
  }

  goAddCai(): void {
    const selectedCais: string[] = [];
    const inputs = document.getElementsByTagName('input');

    for (let i = 0; i < inputs.length; i++) {
      if (inputs[i].type === 'radio' && inputs[i].checked) {
        selectedCais.push(inputs[i].value);
      }
    }

    if (selectedCais.length < 1) {
      return;
    }

    this.selectedCaiItem = selectedCais[0];
    localStorage.setItem('requestorRatt', selectedCais[0]);

    window.close();
  }

  checkQA(code: string): boolean {
    return this.selectedCaiItem === code;
  }

  trackByIndex(index: number, _item: any): number {
    return index;
  }

  ngOnDestroy(): void {
    this.getAllListSubscription?.unsubscribe();
  }
}
